import React, { Component } from 'react';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import { Dropdown, OverlayTrigger, Popover } from 'react-bootstrap';
import {
  CommentIcon,
  LockIcon,
  LockOpenIcon,
  LuckIcon,
  PencilIcon,
  PinIcon,
  TrashIcon
} from 'components/Icon';
import IconButton from 'components/IconButton';
import { SM_BREAKPOINT_AND_DOWN } from 'utils/constants';
import { isCommunityManager } from 'utils/roles';
import { userShape, wallPostShape } from 'utils/shapes';
import {
  canDeletePostOrComment,
  canEditPostOrComment,
  getUsersTooltipMessage
} from 'utils/wall';
import withMediaQuery from 'utils/withMediaQuery';
import './Post.css';

class PostActions extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isDeleting: false,
      isEditing: false,
      currentPostId: '',
      isPopoverVisible: false
    };

    this.likeUnlikePost = debounce(this.likeUnlikePost, 500, {
      leading: true,
      trailing: false
    });
  }

  componentDidMount() {
    document.body.addEventListener('click', this.handleOutsideClick);
    document.body.addEventListener('scroll', this.handleOutsideClick, true);
  }

  componentWillUnmount() {
    document.body.removeEventListener('click', this.handleOutsideClick);
    document.body.removeEventListener('scroll', this.handleOutsideClick, true);
  }

  handleOutsideClick = event => {
    const parent = document.getElementById('post-actions-dropdown');
    if (
      this.state.isPopoverVisible &&
      parent &&
      !parent.contains(event.target)
    ) {
      this.setState({
        isPopoverVisible: false
      });
    }
  };

  likeUnlikePost = post => {
    const { onLikePost, onUnlikePost } = this.props;

    post.likedByMe ? onUnlikePost(post) : onLikePost(post);
  };

  deletePost = post => {
    const { onDeletePost } = this.props;

    this.setState({ isDeleting: true }, () => {
      onDeletePost(post);
    });
  };

  handlePopoverToggle = popoverId => {
    this.setState(prevState => ({
      currentPostId: popoverId,
      isPopoverVisible: !prevState.isPopoverVisible
    }));
  };

  handleCloseOverlay = () => {
    this.setState({
      isPopoverVisible: false
    });
  };

  render() {
    const {
      post,
      user,
      isEditingPostOrComment,
      toggleComments,
      onLikePost,
      onEditPost,
      onDeletePost,
      onPinWallPost,
      onUnpinWallPost,
      onLockWallPost,
      onUnlockWallPost,
      trashIconSize,
      showLegacyFeed,
      matchesBreakpoint: isMobileView
    } = this.props;

    const buttons = [];
    const mobileCommentStats = [];
    const mobileFooterActions = [];
    const mobilePostMenuActions = [];

    if (onPinWallPost && user && isCommunityManager(user)) {
      buttons.push(
        <IconButton
          key="pin"
          icon={PinIcon}
          size="base"
          onClick={() => {
            post.isPinned ? onUnpinWallPost(post) : onPinWallPost(post);
          }}
          filled={post.isPinned}
          alt="Pin post to top"
          confirmation={`Are you sure you want to ${
            post.isPinned ? 'unpin' : 'pin'
          } this post?`}
        />
      );
      if (isMobileView) {
        mobilePostMenuActions.push(
          <div>
            <IconButton
              key="pin"
              icon={PinIcon}
              size="base"
              label={`${post.isPinned ? 'Unpin' : 'Pin'} post`}
              onClick={() => {
                post.isPinned ? onUnpinWallPost(post) : onPinWallPost(post);
              }}
              filled={post.isPinned}
              alt="Pin post to top"
              confirmation={`Are you sure you want to ${
                post.isPinned ? 'unpin' : 'pin'
              } this post?`}
            />
          </div>
        );
      }
    }

    if (onLikePost) {
      let likeLabel =
        (post.likeCount ? post.likeCount + ' ' : '') +
        'Luck' +
        (post.likeCount > 1 ? 's' : '');
      buttons.push(
        <IconButton
          key="like"
          icon={LuckIcon}
          size="base"
          count={post.likeCount}
          label={likeLabel}
          tooltipMessage={getUsersTooltipMessage(
            post.likedBy,
            u => u.displayName
          )}
          tooltipClass="post-like-icon-tooltip"
          title
          alt="Luck this post"
          filled={post.likedByMe}
          onClick={() => this.likeUnlikePost(post)}
        />
      );
      if (isMobileView) {
        mobileCommentStats.push(
          <IconButton
            key="like"
            icon={LuckIcon}
            size="base"
            label={post.likeCount === 0 ? '0' : post.likeCount}
            filled={post.likedByMe}
            tooltipMessage={getUsersTooltipMessage(
              post.likedBy,
              u => u.displayName
            )}
            tooltipClass="post-like-icon-tooltip"
            title
            alt="Luck"
          />
        );
        mobileFooterActions.push(
          <IconButton
            key="like"
            icon={LuckIcon}
            size="base"
            label={'Luck'}
            tooltipMessage={getUsersTooltipMessage(
              post.likedBy,
              u => u.displayName
            )}
            tooltipClass="post-like-icon-tooltip"
            title
            alt="Luck this post"
            filled={post.likedByMe}
            onClick={() => this.likeUnlikePost(post)}
          />
        );
      }
    }

    if (onEditPost && canEditPostOrComment(post)) {
      buttons.push(
        <IconButton
          key="edit"
          icon={PencilIcon}
          size="base"
          onClick={() => {
            onEditPost(post);
          }}
          disabled={isEditingPostOrComment()}
          title
          alt="Edit this post"
        />
      );
      if (isMobileView) {
        mobilePostMenuActions.push(
          <div>
            <IconButton
              key="edit"
              icon={PencilIcon}
              size="base"
              label={'Edit Post'}
              onClick={() => {
                onEditPost(post);
              }}
              disabled={isEditingPostOrComment()}
              title
              alt="Edit this post"
            />
          </div>
        );
      }
    }
    if (onDeletePost && canDeletePostOrComment(user, post)) {
      const { isDeleting } = this.state;
      buttons.push(
        <IconButton
          key="delete"
          icon={TrashIcon}
          onClick={() => {
            !isDeleting && this.deletePost(post);
          }}
          disabled={isDeleting}
          title
          size={trashIconSize}
          alt="Delete this post"
          confirmation="Are you sure you want to delete this post?"
        />
      );
      if (isMobileView) {
        mobilePostMenuActions.push(
          <div>
            <IconButton
              key="delete"
              icon={TrashIcon}
              onClick={() => {
                !isDeleting && this.deletePost(post);
              }}
              label={'Delete Post'}
              disabled={isDeleting}
              title
              size={trashIconSize}
              alt="Delete this post"
              confirmation="Are you sure you want to delete this post?"
            />
          </div>
        );
      }
    }
    if (!post.isLocked && toggleComments) {
      const comments = (post.comments && post.comments.comments) || [];
      let commentLabel =
        (post.commentCount ? post.commentCount + ' ' : '') +
        'Comment' +
        (post.commentCount > 1 ? 's' : '');
      buttons.push(
        <IconButton
          key="comment"
          icon={CommentIcon}
          size="base"
          count={post.commentCount}
          label={commentLabel}
          tooltipMessage={getUsersTooltipMessage(comments, object => {
            return object.author.displayName;
          })}
          tooltipClass="post-comment-icon-tooltip"
          onClick={() => toggleComments(post.id)}
          alt="Add Comment"
        />
      );
      if (isMobileView) {
        mobileCommentStats.push(
          <IconButton
            key="comment"
            icon={CommentIcon}
            size="base"
            count={post.commentCount}
            label={`${post.commentCount} Comment${
              post.commentCount > 1 ? 's' : ''
            }`}
            tooltipMessage={getUsersTooltipMessage(comments, object => {
              return object.author.displayName;
            })}
            tooltipClass="post-comment-icon-tooltip"
            alt="Comment"
          />
        );
        mobileFooterActions.push(
          <IconButton
            key="comment"
            icon={CommentIcon}
            size="base"
            label={'Comment'}
            tooltipMessage={getUsersTooltipMessage(comments, object => {
              return object.author.displayName;
            })}
            tooltipClass="post-comment-icon-tooltip"
            onClick={() => toggleComments(post.id)}
            alt="Add Comment"
          />
        );
      }
    }

    if (onLockWallPost && user && isCommunityManager(user)) {
      buttons.push(
        <IconButton
          key="lock"
          icon={post.isLocked ? LockIcon : LockOpenIcon}
          size="base"
          onClick={() => {
            post.isLocked ? onUnlockWallPost(post) : onLockWallPost(post);
          }}
          alt={post.isLocked ? 'Unlock Post' : 'Lock Post'}
          confirmation={`Are you sure you want to ${
            post.isLocked ? 'unlock' : 'lock'
          } this post?`}
        />
      );
      if (isMobileView) {
        mobilePostMenuActions.push(
          <div>
            <IconButton
              key="lock"
              icon={post.isLocked ? LockIcon : LockOpenIcon}
              size="base"
              onClick={() => {
                post.isLocked ? onUnlockWallPost(post) : onLockWallPost(post);
              }}
              label={post.isLocked ? 'Unlock Post' : 'Lock Post'}
              alt={post.isLocked ? 'Unlock Post' : 'Lock Post'}
              confirmation={`Are you sure you want to ${
                post.isLocked ? 'unlock' : 'lock'
              } this post?`}
            />
          </div>
        );
      }
    }

    const actionsMenu = (
      <Popover id="post-actions-dropdown">
        <Popover.Body>
          <Dropdown>{mobilePostMenuActions}</Dropdown>
        </Popover.Body>
      </Popover>
    );

    if (isMobileView) {
      const feedStatsClassName = showLegacyFeed
        ? 'legacy-feed-post-stats'
        : 'feed-post-stats';
      const footerPostActionsClassName = showLegacyFeed
        ? 'legacy-feed-post-footer-actions'
        : 'feed-post-footer-actions';
      return (
        <>
          {mobilePostMenuActions?.length > 0 && (
            <OverlayTrigger
              trigger="click"
              placement="bottom"
              overlay={actionsMenu}
              show={this.state.isPopoverVisible}
              onHide={this.handleCloseOverlay}
              rootClose
            >
              <button
                type="button"
                className="post-actions-menu"
                onClick={() => this.handlePopoverToggle(post.id)}
              >
                •••
              </button>
            </OverlayTrigger>
          )}
          <div className={feedStatsClassName}>
            <span>{mobileCommentStats}</span>
          </div>
          <div className={footerPostActionsClassName}>
            {mobileFooterActions}
          </div>
        </>
      );
    } else if (buttons.length === 1 && post.isSystemPost) {
      return (
        <div className="feed-message-icon-container single-button">
          {buttons}
        </div>
      );
    } else if (buttons.length) {
      return (
        <div
          className={`feed-message-icon-container${
            buttons.length > 4 ? ' wrap' : ''
          }`}
        >
          {buttons}
        </div>
      );
    } else {
      return null;
    }
  }
}

PostActions.propTypes = {
  user: userShape,
  post: wallPostShape,
  isEditingPostOrComment: PropTypes.func,
  onDeletePost: PropTypes.func,
  toggleComments: PropTypes.func,
  onLikePost: PropTypes.func,
  onUnlikePost: PropTypes.func,
  onPinWallPost: PropTypes.func,
  onUnpinWallPost: PropTypes.func,
  onLockWallPost: PropTypes.func,
  onUnlockWallPost: PropTypes.func,
  showLegacyFeed: PropTypes.bool
};

export default withMediaQuery(SM_BREAKPOINT_AND_DOWN)(PostActions);
